import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useWatch } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  translateSpecialTagOptions,
  type CatalogEntry,
} from '../../../utils/catalogLabels';
import debug from 'debug';
import { gql } from '@apollo/client';

// Logger
const logCreateOT = debug('rois:newcommunication:createot');

//Components
import Tabs from '../../display/Tabs';
import { SelectionOT } from '../../display/SelectionOT';
import { forgetCommunicationCreationPick } from '../../../utils/communicationCreationDraft';
import DropDownInput from '../../inputs/DropDown/DropDownInput';
import MultiSelectDropdown from '../../inputs/DropDown/MultiSelectDropdown';
import SearchableDropdown from '../../inputs/SearchableDropdown';

// Styled Components
import { ContainerOT, ErrorMessage, TabContent } from './styles';
import { Typography } from '../../display/Typography';

// Types
import { FormCreateOTProps } from './types';
import { modalIds } from '../../modals/SuperModal/types';
import { OptionSelected } from '../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../inputs/DropDown/DropDownInput/types';
import { ImperativeRefSearchableDropdown } from '../../inputs/SearchableDropdown/types';
import { ControllerProps } from 'react-hook-form/dist/types/controller';
import { NewCommunication } from '../../../types/communication';

// Selectors
import { getCurrentRowDataModal } from '../../../state/selectors/ui/modal';

// Actions
import { openModal } from '../../../state/actions/ui/modal';

// Graphql
import { client } from '../../../gql/client';
import {
  GET_CAPITOL_BY_ID,
  GET_CLASSIFICATION_BY_ID_OR_ROID,
  GET_SUB_CAPITOL_BY_ID,
  GET_SUB_CAPITOLS_BY_IDS,
} from '../../../gql/queries/orders/predefinedData';

// Constants
import { InputsContainer } from '../components/MainStyled';
import { fetchDefaultData } from '../../../utils/fetchData';
import * as Sentry from '@sentry/react';
import { formNaming } from './constants';

export const CreateOT = ({
  control,
  setValue,
  errors,
  namingPersistForm,
  selectorIndexTab,
  namingTabsType,
  isNecessaryTagSpecial = false,
  teamDropDownRef,
  autoFillData,
  onUserClickTab,
}: FormCreateOTProps) => {
  // Habilitar logs en desarrollo
  if (import.meta.env.DEV) {
    debug.enable('rois:newcommunication:*');
  }

  const { t } = useTranslation(['forms', 'workOrders']);
  const location = useLocation();
  const isEditForm = location.pathname
    .split('/')
    .some((path) => path === 'edit-communication');

  // Log de CreateOT
  logCreateOT('🟣 [CreateOT] RENDER');
  logCreateOT('  - selectorIndexTab (prop recibido):', selectorIndexTab);
  logCreateOT('  - namingTabsType:', namingTabsType);
  logCreateOT('  - SE PASARÁ a <Tabs defaultIndex={%d}>', selectorIndexTab);

  const dispatch = useDispatch();

  const capitolDropDownRef = useRef<ImperativeRefSearchableDropdown>(null);
  const subCapitolDropDownRef = useRef<ImperativeRefSearchableDropdown>(null);
  const classificationDropDownRef = useRef<ImperativeRefInput>(null);
  const predefinidosDropDownRef = useRef<ImperativeRefSearchableDropdown>(null);

  const predefine = useWatch({
    control,
    name: 'predefine',
  });

  const specialTag = useWatch({
    control,
    name: 'specialTag',
  });

  const classification = useWatch({
    control,
    name: 'classification',
  });

  const capitol = useWatch({
    control,
    name: 'capitol',
  });

  const subCapitols = useWatch({
    control,
    name: 'subCapitols',
  });

  const subCapitol = useWatch({
    control,
    name: 'subCapitol',
  });

  // Determine modal keys based on form type using constants
  const otModalKey =
    namingPersistForm === formNaming.NEW_COMMUNICATION_FORM ? formNaming.NEW_COMMUNICATION_FORM_OT :
      namingPersistForm === formNaming.EDIT_COMMUNICATION_FORM ? formNaming.EDIT_COMMUNICATION_FORM_OT :
        namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM ? formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_OT :
          namingPersistForm;

  const campaignModalKey =
    namingPersistForm === formNaming.NEW_COMMUNICATION_FORM ? formNaming.NEW_COMMUNICATION_FORM_CAMPAIGN :
      namingPersistForm === formNaming.EDIT_COMMUNICATION_FORM ? formNaming.EDIT_COMMUNICATION_FORM_CAMPAIGN :
        namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM ? formNaming.NEW_DUPLICATE_COMMUNICATION_FORM_CAMPAIGN :
          namingPersistForm;

  // Selectors for each tab
  const otSelector = useSelector(
    getCurrentRowDataModal(otModalKey),
    isEqual
  );

  const campaignSelector = useSelector(
    getCurrentRowDataModal(campaignModalKey),
    isEqual
  );

  // T36: Auto-fill predefinido/capitol/subcapitol/classification from parent order when creating related communication
  // parentData may come from:
  //   - ShowOT: has capitolId/subCapitolId/classification/predefinedOrder as IDs
  //   - Modal table (selectAllOrders): has capitolId/subCapitolId/classificationId/predefinedOrderId as IDs,
  //     plus capitol/subCapitol/classification as display NAMES
  // T103: Solo auto-rellenar desde el padre si la comunicación no tiene valores propios.
  //   Edit/Duplicate: NUNCA sobreescribir (la comunicación mantiene sus propios valores).
  //   New: auto-rellenar solo si predefine/capitol están vacíos.
  useEffect(() => {
    const parentData = otSelector?.data || campaignSelector?.data;
    const isNewCommunication = namingPersistForm === formNaming.NEW_COMMUNICATION_FORM;

    if (!parentData) {
      // Solo limpiar en modo NEW (edit/duplicate mantienen sus valores propios)
      if (isNewCommunication) {
        setValue('classification', '');
        setValue('predefine', '');
        setValue('capitol', '');
        setValue('subCapitol', '');
        setValue('subCapitols', []);
        setValue('teamRelationships', []);
        classificationDropDownRef.current?.clearSelected();
        predefinidosDropDownRef.current?.clearSelected();
        capitolDropDownRef.current?.clearSelected();
        subCapitolDropDownRef.current?.clearSelected();
        teamDropDownRef?.current?.clearSelected();
      }
      return;
    }

    // Solo auto-rellenar en modo NEW y si la comunicación no tiene valores propios
    if (!isNewCommunication) return;
    if (predefine || capitol) return;

    // Equipos del padre: el array entero (antes solo el primero). parentData
    // puede traer `teamIDs` (fragments de ShowOT) o `teamRelationships` (modal
    // selectAllOrders). El MultiSelectDropdown resuelve las etiquetas contra
    // sus opciones, así que no hace falta consultar cada equipo por id.
    const parentTeamIds: string[] = Array.isArray(parentData.teamIDs)
      ? parentData.teamIDs
      : parentData.teamRelationships
        ? [parentData.teamRelationships]
        : [];
    if (parentTeamIds.length > 0) {
      setValue('teamRelationships', parentTeamIds);
      teamDropDownRef?.current?.setSelectedValues(parentTeamIds);
    }

    // Resolve IDs: prefer explicit ID fields, fallback to generic field (which is ID from ShowOT)
    const parentCapitolId = parentData.capitolId || parentData.capitol;
    const parentSubCapitolId = parentData.subCapitolId || parentData.subCapitol;
    // classificationId comes from modal table, classification from ShowOT (both are IDs)
    const parentClassificationId = parentData.classificationId || parentData.classification;
    // predefinedOrderId comes from modal table, predefinedOrder from ShowOT (both are IDs)
    const parentPredefinedOrderId = parentData.predefinedOrderId || parentData.predefinedOrder;

    if (parentClassificationId) {
      setValue('classification', parentClassificationId);
      // Update classification dropdown visual
      const loadClassification = async () => {
        try {
          const { data: { classification: cls } } = await client.query({
            query: GET_CLASSIFICATION_BY_ID_OR_ROID,
            variables: { id: parentClassificationId },
          });
          if (cls) {
            classificationDropDownRef.current?.setSelectedOption({
              label: cls.name,
              value: cls.id,
            });
          }
        } catch (err) {
          Sentry.captureException(err);
        }
      };
      loadClassification();
    }
    if (parentPredefinedOrderId) {
      // Read predefined order name from Apollo cache for visual update
      const predefinedOrderData = client.readFragment({
        id: `PredefinedOrder:${parentPredefinedOrderId}`,
        fragment: gql`
          fragment PredefinedOrderAutoFill on PredefinedOrder {
            id
            name
          }
        `,
      });
      if (predefinedOrderData) {
        predefinidosDropDownRef.current?.setSelectedOption({
          label: predefinedOrderData.name,
          value: parentPredefinedOrderId,
          data: {
            capitolId: parentCapitolId,
            subCapitolId: parentSubCapitolId,
          },
        });
      }
      // Update form value and trigger capitol/subcapitol cascade
      setValue('predefine', parentPredefinedOrderId);
      const predefinedOption = {
        value: parentPredefinedOrderId,
        data: {
          capitolId: parentCapitolId,
          subCapitolId: parentSubCapitolId,
        },
      } as OptionSelected;
      onSelectPredefined(predefinedOption, (val: string) => setValue('predefine', val));
    } else {
      if (parentCapitolId) {
        setValue('capitol', parentCapitolId);
        // Load subcapitols for the capitol
        const loadSubCapitols = async () => {
          try {
            const { data: { capitol: capitolData } } = await client.query({
              query: GET_CAPITOL_BY_ID,
              variables: { id: parentCapitolId },
            });
            if (capitolData) {
              capitolDropDownRef.current?.setSelectedOption({
                label: capitolData.name,
                value: capitolData.id,
                data: capitolData.subCapitolsIDs,
              });
              const { readSubCapitols } = client.readQuery({
                query: GET_SUB_CAPITOLS_BY_IDS,
                variables: { ids: capitolData.subCapitolsIDs ?? [] },
              });
              if (readSubCapitols) {
                const sorted = [...readSubCapitols].sort((a, b) => a.name.localeCompare(b.name));
                setValue('subCapitols', sorted);
              }
            }
          } catch (err) {
            Sentry.captureException(err);
          }
        };
        loadSubCapitols();
      }
      if (parentSubCapitolId) {
        setValue('subCapitol', parentSubCapitolId);
        // Set the visual selection in the dropdown
        const loadSubCapitol = async () => {
          try {
            const { data: { subCapitol: subCapitolData } } = await client.query({
              query: GET_SUB_CAPITOL_BY_ID,
              variables: { id: parentSubCapitolId },
            });
            if (subCapitolData) {
              subCapitolDropDownRef.current?.setSelectedOption({
                label: subCapitolData.name,
                value: subCapitolData.id,
                data: subCapitolData.elementOrdersIds,
              });
            }
          } catch (err) {
            Sentry.captureException(err);
          }
        };
        loadSubCapitol();
      }
    }
  }, [otSelector?.data, campaignSelector?.data]);

  // Tarea #8 (2026-04-24): Auto-fill desde el botón "Autorellenar" (flujo ShowOT →
  // Nueva comunicación). autoFillData trae el Order + GenericOrder/Campaign padre
  // (fetcheados por autoFillFunction en NewCommunicationForm/index.tsx).
  // Este useEffect NO dispatcha a Redux modalTable — no relaciona la OT, sólo rellena
  // los campos del form (predefine/classification/capitol/subCapitol/teamRelationships).
  // Se dispara cada vez que el usuario pulsa "Autorellenar" (setAutoFillData → nuevo objeto).
  useEffect(() => {
    const order = autoFillData?.order;
    if (!order) return;
    const isNewCommunication = namingPersistForm === formNaming.NEW_COMMUNICATION_FORM;
    if (!isNewCommunication) return;

    const autoCapitolId = order.capitolIDs?.[0];
    const autoSubCapitolId = order.subCapitolIDs?.[0];
    const autoClassificationId = order.classificationIDs?.[0];
    const autoPredefinedOrderId = order.predefinedOrderIDs?.[0];
    const autoTeamIds: string[] =
      autoFillData?.genericOrder?.teamIDs ??
      autoFillData?.campaignData?.teamIDs ??
      [];

    if (autoClassificationId) {
      setValue('classification', autoClassificationId);
      const loadClassificationAuto = async () => {
        try {
          const { data: { classification: cls } } = await client.query({
            query: GET_CLASSIFICATION_BY_ID_OR_ROID,
            variables: { id: autoClassificationId },
          });
          if (cls) {
            classificationDropDownRef.current?.setSelectedOption({
              label: cls.name,
              value: cls.id,
            });
          }
        } catch (err) {
          Sentry.captureException(err);
        }
      };
      loadClassificationAuto();
    }

    if (autoPredefinedOrderId) {
      const predefinedOrderData = client.readFragment({
        id: `PredefinedOrder:${autoPredefinedOrderId}`,
        fragment: gql`
          fragment PredefinedOrderAutoFillByButton on PredefinedOrder {
            id
            name
          }
        `,
      });
      if (predefinedOrderData) {
        predefinidosDropDownRef.current?.setSelectedOption({
          label: predefinedOrderData.name,
          value: autoPredefinedOrderId,
          data: {
            capitolId: autoCapitolId,
            subCapitolId: autoSubCapitolId,
          },
        });
      }
      setValue('predefine', autoPredefinedOrderId);
      const predefinedOption = {
        value: autoPredefinedOrderId,
        data: {
          capitolId: autoCapitolId,
          subCapitolId: autoSubCapitolId,
        },
      } as OptionSelected;
      onSelectPredefined(predefinedOption, (val: string) => setValue('predefine', val));
    } else {
      if (autoCapitolId) {
        setValue('capitol', autoCapitolId);
        const loadCapitolAuto = async () => {
          try {
            const { data: { capitol: capitolData } } = await client.query({
              query: GET_CAPITOL_BY_ID,
              variables: { id: autoCapitolId },
            });
            if (capitolData) {
              capitolDropDownRef.current?.setSelectedOption({
                label: capitolData.name,
                value: capitolData.id,
                data: capitolData.subCapitolsIDs,
              });
              const { readSubCapitols } = client.readQuery({
                query: GET_SUB_CAPITOLS_BY_IDS,
                variables: { ids: capitolData.subCapitolsIDs ?? [] },
              });
              if (readSubCapitols) {
                const sorted = [...readSubCapitols].sort((a, b) => a.name.localeCompare(b.name));
                setValue('subCapitols', sorted);
              }
            }
          } catch (err) {
            Sentry.captureException(err);
          }
        };
        loadCapitolAuto();
      }
      if (autoSubCapitolId) {
        setValue('subCapitol', autoSubCapitolId);
        const loadSubCapitolAuto = async () => {
          try {
            const { data: { subCapitol: subCapitolData } } = await client.query({
              query: GET_SUB_CAPITOL_BY_ID,
              variables: { id: autoSubCapitolId },
            });
            if (subCapitolData) {
              subCapitolDropDownRef.current?.setSelectedOption({
                label: subCapitolData.name,
                value: subCapitolData.id,
                data: subCapitolData.elementOrdersIds,
              });
            }
          } catch (err) {
            Sentry.captureException(err);
          }
        };
        loadSubCapitolAuto();
      }
    }

    if (autoTeamIds.length > 0) {
      setValue('teamRelationships', autoTeamIds);
      teamDropDownRef?.current?.setSelectedValues(autoTeamIds);
    }
  }, [autoFillData]);

  const onSelectCapitol = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const { readSubCapitols } = client.readQuery({
          query: GET_SUB_CAPITOLS_BY_IDS,
          variables: {
            ids: option?.data ?? [],
          },
        });
        if (readSubCapitols.length === 0) {
          await fetchDefaultData();
          const { readSubCapitols } = client.readQuery({
            query: GET_SUB_CAPITOLS_BY_IDS,
            variables: {
              ids: option?.data ?? [],
            },
          });
          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );

          setValue('subCapitols', sortedSubCapitols);
        } else {
          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );

          setValue('subCapitols', sortedSubCapitols);
        }
        subCapitolDropDownRef.current?.clearSelected();
        setValue('subCapitol', '');
        onChange(option?.value ?? '');
      };

      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const onSelectPredefined = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        onChange(option?.value ?? '');

        const {
          data: { capitol },
        } = await client.query({
          query: GET_CAPITOL_BY_ID,
          variables: {
            id: option?.data.capitolId,
          },
        });

        const { readSubCapitols } = client.readQuery({
          query: GET_SUB_CAPITOLS_BY_IDS,
          variables: {
            ids: capitol?.subCapitolsIDs ?? [],
          },
        });

        if (readSubCapitols.length === 0) {
          await fetchDefaultData();
          const { readSubCapitols } = client.readQuery({
            query: GET_SUB_CAPITOLS_BY_IDS,
            variables: {
              ids: capitol?.subCapitolsIDs ?? [],
            },
          });

          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setValue('subCapitols', sortedSubCapitols);
        } else {
          const sortedSubCapitols = [...readSubCapitols].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setValue('subCapitols', sortedSubCapitols);
        }

        capitolDropDownRef.current?.setSelectedOption({
          label: capitol.name,
          value: capitol.id,
          data: capitol.subCapitolsIDs,
        });

        const {
          data: { subCapitol },
        } = await client.query({
          query: GET_SUB_CAPITOL_BY_ID,
          variables: {
            id: option?.data.subCapitolId,
          },
        });

        subCapitolDropDownRef.current?.setSelectedOption({
          label: subCapitol.name,
          value: subCapitol.id,
          data: subCapitol.elementOrdersIds,
        });
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    [capitolDropDownRef, subCapitolDropDownRef]
  );

  const renderSubCapitols = useCallback<
    ControllerProps<NewCommunication, 'subCapitol'>['render']
  >(
    ({ field: { onChange }, formState: { defaultValues } }) => {
      const options =
        subCapitols?.map((subCapitol) => ({
          label: subCapitol?.name || '',
          value: subCapitol?.id || '',
          data: subCapitol?.elementOrdersIds || '',
        })) || [];
      return (
        <SearchableDropdown
          ref={subCapitolDropDownRef}
          inputSize="L"
          onChangeSelected={(option) => onChange(option?.value ?? '')}
          typeDropDown="Default"
          labelText={t('workOrder.subchapter')}
          placeholder={t('common:select')}
          defaultValue={subCapitol || defaultValues?.subCapitol}
          borderError={!!errors.subCapitol}
          options={options}
        />
      );
    },
    [subCapitols, errors]
  );

  const renderCapitols = useCallback<
    ControllerProps<NewCommunication, 'capitol'>['render']
  >(
    ({ field: { onChange }, formState: { defaultValues } }) => {
      const options =
        defaultValues?.capitols?.map((capitol) => ({
          label: capitol?.name || '',
          value: capitol?.id || '',
          data: capitol?.subCapitolsIDs || '',
        })) || [];
      return (
        <SearchableDropdown
          ref={capitolDropDownRef}
          inputSize="L"
          onChangeSelected={(option) => onSelectCapitol(option, onChange)}
          typeDropDown="Default"
          labelText={t('workOrder.chapter')}
          placeholder={t('common:select')}
          defaultValue={capitol || defaultValues?.capitol}
          borderError={!!errors.capitol}
          options={options}
        />
      );
    },
    [capitol, errors]
  );

  const tabData = [
    {
      label: isNecessaryTagSpecial
        ? t('workOrders:createNew')
        : t('communication.otData'),
      content: (
        <InputsContainer>
          <Controller
            control={control}
            name="predefine"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options =
                defaultValues?.predefinedOrders?.map((predefinedOrder) => ({
                  label: predefinedOrder?.name || '',
                  value: predefinedOrder?.id || '',
                  data: {
                    capitolId: predefinedOrder?.capitolId || '',
                    subCapitolId: predefinedOrder?.subCapitolId || '',
                    elementOrderId: predefinedOrder?.elementOrderId || '',
                  },
                })) || [];
              return (
                <SearchableDropdown
                  ref={predefinidosDropDownRef}
                  inputSize="XL"
                  onChangeSelected={(option) =>
                    onSelectPredefined(option, onChange)
                  }
                  borderError={!!errors.predefine}
                  typeDropDown="Default"
                  labelText={t('workOrder.predefined')}
                  placeholder={t('common:select')}
                  defaultValue={predefine || defaultValues?.predefine}
                  options={options}
                  minSearchLength={2}
                />
              );
            }}
          />
          {isNecessaryTagSpecial && (
            <Controller
              control={control}
              name="specialTag"
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options = translateSpecialTagOptions(
                  defaultValues?.specialTagType as CatalogEntry[] | undefined,
                  t
                );
                return (
                  <DropDownInput
                    inputSize="S"
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    borderError={!!errors.specialTag}
                    typeDropDown="Default"
                    labelText={t('workOrder.specialTag')}
                    placeholder={t('common:select')}
                    defaultValue={specialTag || defaultValues?.specialTag}
                    options={options}
                  />
                );
              }}
            />
          )}

          <Controller
            control={control}
            name="classification"
            render={({ field: { onChange }, formState: { defaultValues } }) => {
              const options =
                defaultValues?.classifications?.map((classification) => ({
                  label: classification?.name || '',
                  value: classification?.id || '',
                })) || [];
              return (
                <DropDownInput
                  ref={classificationDropDownRef}
                  inputSize="S"
                  onChangeSelected={(option) => onChange(option?.value ?? '')}
                  typeDropDown="Default"
                  labelText={t('workOrder.classification')}
                  placeholder={t('common:select')}
                  defaultValue={classification || defaultValues?.classification}
                  borderError={!!errors.classification}
                  options={options}
                />
              );
            }}
          />

          <Controller
            control={control}
            name="capitol"
            render={renderCapitols}
          />

          <Controller
            control={control}
            name="subCapitol"
            render={renderSubCapitols}
          />

          {(namingPersistForm === formNaming.NEW_COMMUNICATION_FORM ||
            namingPersistForm === formNaming.NEW_DUPLICATE_COMMUNICATION_FORM) && (
            <Controller
              control={control}
              name="teamRelationships"
              render={({ field: { onChange, value }, formState: { defaultValues } }) => {
                const options =
                  defaultValues?.teams?.map((team) => ({
                    label: team?.name || '',
                    value: team?.id || '',
                  })) || [];
                if (options.length === 0) return <></>;
                // Borradores antiguos guardaban un string: se normaliza a array.
                const selectedValues = Array.isArray(value)
                  ? value
                  : value
                    ? [value as unknown as string]
                    : [];
                return (
                  <MultiSelectDropdown
                    ref={teamDropDownRef}
                    inputSize="L"
                    typeDropDown="Default"
                    labelText={t('workOrder.team')}
                    placeholder={t('common:select')}
                    options={options}
                    selectedValues={selectedValues}
                    onChangeSelected={(ids) => onChange(ids)}
                  />
                );
              }}
            />
          )}
        </InputsContainer>
      ),
    },
    {
      label: t('common:tabs.relateWithWorkOrder'),
      content: (
        <TabContent>
          <Controller
            control={control}
            name="relateOT"
            render={({ field: { onChange } }) => (
              <SelectionOT
                label={
                  otSelector?.data ? '' : t('forms:placeholders.selectWorkOrder')
                }
                onChange={onChange}
                data={otSelector}
                openModal={() => {
                  dispatch(
                    openModal(modalIds.RELATES_COMMUNICATION_MODAL, {
                      namingPersistForm: otModalKey
                    })
                  );
                }}
                namingPersistForm={otModalKey}
                // Quitar la relación olvida también la elección apuntada
                // para esta alta (solo claves del alta; el resto no hace nada).
                onRemove={() => forgetCommunicationCreationPick(otModalKey)}
                type={'OT'}
              />
            )}
          />
          {errors.relateOT && (
            <ErrorMessage size="XS" colorText="red">
              *{t('validation.requiredOT')}
            </ErrorMessage>
          )}
          <Typography size="S" variant="semiBold">
            {t('workOrders:communicationData')}
          </Typography>
          <InputsContainer>
            <Controller
              control={control}
              name="predefine"
              render={({ field: { onChange }, formState: { defaultValues } }) => {
                const options =
                  defaultValues?.predefinedOrders?.map((predefinedOrder) => ({
                    label: predefinedOrder?.name || '',
                    value: predefinedOrder?.id || '',
                    data: {
                      capitolId: predefinedOrder?.capitolId || '',
                      subCapitolId: predefinedOrder?.subCapitolId || '',
                      elementOrderId: predefinedOrder?.elementOrderId || '',
                    },
                  })) || [];
                return (
                  <SearchableDropdown
                    ref={predefinidosDropDownRef}
                    inputSize="XL"
                    onChangeSelected={(option) =>
                      onSelectPredefined(option, onChange)
                    }
                    borderError={!!errors.predefine}
                    typeDropDown="Default"
                    labelText={t('workOrder.predefined')}
                    placeholder={t('common:select')}
                    defaultValue={predefine || defaultValues?.predefine}
                    options={options}
                    minSearchLength={2}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="classification"
              render={({ field: { onChange }, formState: { defaultValues } }) => {
                const options =
                  defaultValues?.classifications?.map((cls) => ({
                    label: cls?.name || '',
                    value: cls?.id || '',
                  })) || [];
                return (
                  <DropDownInput
                    ref={classificationDropDownRef}
                    inputSize="S"
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    typeDropDown="Default"
                    labelText={t('workOrder.classification')}
                    placeholder={t('common:select')}
                    defaultValue={classification || defaultValues?.classification}
                    borderError={!!errors.classification}
                    options={options}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="capitol"
              render={renderCapitols}
            />
            <Controller
              control={control}
              name="subCapitol"
              render={renderSubCapitols}
            />
          </InputsContainer>
        </TabContent>
      ),
    },
    {
      label: t('common:tabs.relateWithCampaign'),
      content: (
        <TabContent>
          <Controller
            control={control}
            name="relateCampaign"
            render={({ field: { onChange } }) => (
              <SelectionOT
                label={
                  campaignSelector?.data ? '' : t('forms:placeholders.selectCampaign')
                }
                onChange={onChange}
                data={campaignSelector}
                openModal={() => {
                  dispatch(
                    openModal(modalIds.RELATES_COMMUNICATION_MODAL, {
                      namingPersistForm: campaignModalKey
                    })
                  );
                }}
                namingPersistForm={campaignModalKey}
                onRemove={() => forgetCommunicationCreationPick(campaignModalKey)}
                type={'Campanya'}
              />
            )}
          />
          {errors.relateCampaign && (
            <ErrorMessage size="XS" colorText="red">
              *{t('validation.requiredOT')}
            </ErrorMessage>
          )}
          <Typography size="S" variant="semiBold">
            {t('workOrders:communicationData')}
          </Typography>
          <InputsContainer>
            <Controller
              control={control}
              name="predefine"
              render={({ field: { onChange }, formState: { defaultValues } }) => {
                const options =
                  defaultValues?.predefinedOrders?.map((predefinedOrder) => ({
                    label: predefinedOrder?.name || '',
                    value: predefinedOrder?.id || '',
                    data: {
                      capitolId: predefinedOrder?.capitolId || '',
                      subCapitolId: predefinedOrder?.subCapitolId || '',
                      elementOrderId: predefinedOrder?.elementOrderId || '',
                    },
                  })) || [];
                return (
                  <SearchableDropdown
                    ref={predefinidosDropDownRef}
                    inputSize="XL"
                    onChangeSelected={(option) =>
                      onSelectPredefined(option, onChange)
                    }
                    borderError={!!errors.predefine}
                    typeDropDown="Default"
                    labelText={t('workOrder.predefined')}
                    placeholder={t('common:select')}
                    defaultValue={predefine || defaultValues?.predefine}
                    options={options}
                    minSearchLength={2}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="classification"
              render={({ field: { onChange }, formState: { defaultValues } }) => {
                const options =
                  defaultValues?.classifications?.map((cls) => ({
                    label: cls?.name || '',
                    value: cls?.id || '',
                  })) || [];
                return (
                  <DropDownInput
                    ref={classificationDropDownRef}
                    inputSize="S"
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    typeDropDown="Default"
                    labelText={t('workOrder.classification')}
                    placeholder={t('common:select')}
                    defaultValue={classification || defaultValues?.classification}
                    borderError={!!errors.classification}
                    options={options}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="capitol"
              render={renderCapitols}
            />
            <Controller
              control={control}
              name="subCapitol"
              render={renderSubCapitols}
            />
          </InputsContainer>
        </TabContent>
      ),
    },
  ];

  return (
    <ContainerOT>
      <Tabs
        data={tabData}
        typeTab={namingTabsType}
        defaultIndex={selectorIndexTab}
        onUserClick={onUserClickTab}
        isFirstRemoved={isEditForm}
      />
    </ContainerOT>
  );
};
